import React from 'react';
import {
    VStack,
    FormControl,
    FormLabel,
    Input,
    Select,
    NumberInput,
    NumberInputField,
    HStack,
    Checkbox,
} from '@chakra-ui/react';

interface CustomField {
    name: string;
    field_type: 'text' | 'number' | 'boolean' | 'scale' | 'time_duration';
    category: 'habit' | 'metric' | 'medication' | 'mood' | 'note';
    unit?: string;
    target_value?: {
        min?: number;
        max?: number;
        target?: number;
    };
    frequency: {
        type: 'daily' | 'weekly' | 'monthly';
        times_per_period: number;
    };
    show_in_new_entries: boolean;
    is_archived: boolean;
    entries_count: number;
    is_new: boolean;
}

interface CustomFieldFormProps {
    field: CustomField;
    onChange: (field: CustomField) => void;
    isNameReadOnly?: boolean;
}

const CustomFieldForm: React.FC<CustomFieldFormProps> = ({
    field,
    onChange,
    isNameReadOnly = false,
}) => {
    const handleChange = (updates: Partial<CustomField>) => {
        onChange({ ...field, ...updates });
    };

    return (
        <VStack spacing={4}>
            <FormControl>
                <FormLabel>Field Name</FormLabel>
                <Input 
                    value={field.name} 
                    onChange={e => handleChange({ name: e.target.value })}
                    isReadOnly={isNameReadOnly}
                />
            </FormControl>

            <FormControl>
                <FormLabel>Field Type</FormLabel>
                <Select
                    value={field.field_type}
                    onChange={e => handleChange({ field_type: e.target.value as any })}
                >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="boolean">Boolean</option>
                    <option value="scale">Scale</option>
                    <option value="time_duration">Time Duration</option>
                </Select>
            </FormControl>

            <FormControl>
                <FormLabel>Category</FormLabel>
                <Select
                    value={field.category}
                    onChange={e => handleChange({ category: e.target.value as any })}
                >
                    <option value="habit">Habit</option>
                    <option value="metric">Metric</option>
                    <option value="medication">Medication</option>
                    <option value="mood">Mood</option>
                    <option value="note">Note</option>
                </Select>
            </FormControl>

            {field.field_type === 'number' && (
                <>
                    <FormControl>
                        <FormLabel>Unit</FormLabel>
                        <Input
                            value={field.unit || ''}
                            onChange={e => handleChange({ unit: e.target.value })}
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel>Target Values</FormLabel>
                        <HStack>
                            <NumberInput
                                value={field.target_value?.min ?? ''}
                                onChange={val =>
                                    handleChange({
                                        target_value: {
                                            ...field.target_value,
                                            min: val ? Number(val) : undefined
                                        }
                                    })
                                }
                            >
                                <NumberInputField placeholder="Min" />
                            </NumberInput>
                            <NumberInput
                                value={field.target_value?.max ?? ''}
                                onChange={val =>
                                    handleChange({
                                        target_value: {
                                            ...field.target_value,
                                            max: val ? Number(val) : undefined
                                        }
                                    })
                                }
                            >
                                <NumberInputField placeholder="Max" />
                            </NumberInput>
                            <NumberInput
                                value={field.target_value?.target ?? ''}
                                onChange={val =>
                                    handleChange({
                                        target_value: {
                                            ...field.target_value,
                                            target: val ? Number(val) : undefined
                                        }
                                    })
                                }
                            >
                                <NumberInputField placeholder="Target" />
                            </NumberInput>
                        </HStack>
                    </FormControl>
                </>
            )}

            <FormControl>
                <FormLabel>Frequency</FormLabel>
                <HStack>
                    <Select
                        value={field.frequency.type}
                        onChange={e => {
                            const frequencyType = e.target.value as 'daily' | 'weekly' | 'monthly';
                            handleChange({
                                frequency: {
                                    type: frequencyType,
                                    times_per_period: field.frequency.times_per_period
                                }
                            });
                        }}
                    >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                    </Select>
                    <NumberInput
                        value={field.frequency.times_per_period}
                        min={1}
                        onChange={val =>
                            handleChange({
                                frequency: {
                                    type: field.frequency.type,
                                    times_per_period: val ? Number(val) : 1
                                }
                            })
                        }
                    >
                        <NumberInputField />
                    </NumberInput>
                </HStack>
            </FormControl>

            <FormControl>
                <Checkbox
                    isChecked={field.show_in_new_entries}
                    onChange={e => handleChange({ show_in_new_entries: e.target.checked })}
                >
                    Show in new entries
                </Checkbox>
            </FormControl>

            <FormControl>
                <Checkbox
                    isChecked={field.is_archived}
                    onChange={e => handleChange({ is_archived: e.target.checked })}
                >
                    Archive field
                </Checkbox>
            </FormControl>
        </VStack>
    );
};

export default CustomFieldForm; 
